import React, { useState } from "react";
import { QuizQuestionData } from "../constants/quizQuestionData";
import AnswerButton from "./AnswerButton";
import { AnswerButtonType } from "../types/AnswerButtonType";
import "../css/QuizQuestion.css";
import "../css/QuizFeedback.css";

interface QuizQuestionProps {
  question: QuizQuestionData;
  selectedAnswers: number[];
  onAnswerSelect?: (questionIndex: number, answerIndex: number) => void;
  isSubmitted?: boolean;
  showFeedback?: boolean;
  isFinal?: boolean;
  onButtonClick?: () => void;
  buttonText?: string;
  isButtonDisabled?: boolean;
  isLastQuestion?: boolean;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  selectedAnswers,
  onAnswerSelect = () => {},
  isSubmitted = false,
  showFeedback = false,
  isFinal = false,
  onButtonClick,
  buttonText,
  isButtonDisabled = false,
  isLastQuestion = false
}) => {
  const [isExplanationExpanded, setIsExplanationExpanded] = useState(false);

  return (
    <div className="quiz-question">
      <div className="quiz-content-wrapper">
        <div className="quiz-main-content">
          <div className="quiz-header">
            {(!isFinal && question.subtext) && <p className="subtext">{question.subtext}</p>}
            <div className={`background ${showFeedback ? 'feedback-background' : ''}`}>
              {question.background}
            </div>
          </div>

          <div className="sub-questions">
            {question.subQuestions.map((subQ, subQuestionIdx) => (
              <div key={subQuestionIdx} className="sub-question">
                <p>{subQ.text}</p>
                <div className="answer-buttons">
                  {subQ.answers.map((answer, answerIdx) => (
                    <AnswerButton
                      key={answerIdx}
                      answer={answer}
                      isSelected={selectedAnswers[subQuestionIdx] === answerIdx}
                      type={
                        showFeedback
                          ? (subQ.correct_answer === answerIdx
                              ? AnswerButtonType.CORRECT
                              : (selectedAnswers[subQuestionIdx] === answerIdx
                                ? AnswerButtonType.INCORRECT
                                : AnswerButtonType.DEFAULT))
                          : (selectedAnswers[subQuestionIdx] === answerIdx
                              ? AnswerButtonType.SELECTED
                              : AnswerButtonType.DEFAULT)
                      }
                      isDisabled={isSubmitted || showFeedback}
                      onClick={() => onAnswerSelect(subQuestionIdx, answerIdx)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {showFeedback && (
            <>
              <div 
                className={`explanation-toggle ${isExplanationExpanded ? 'expanded' : ''}`}
                onClick={() => setIsExplanationExpanded(!isExplanationExpanded)}
              >
                {isExplanationExpanded ? '▼' : '▶'} View Explanation
              </div>
              <div className={`explanation-content ${isExplanationExpanded ? 'expanded' : ''}`}>
                <div className="explanation">
                  {question.explanation}
                </div>
              </div>
            </>
          )}
          
          {onButtonClick && (
            <div className="submit-button-container">
              <button 
                onClick={onButtonClick}
                className={`submit-button ${showFeedback ? "next-button" : ""}`}
                disabled={isButtonDisabled}
              >
                {buttonText || (showFeedback 
                  ? (isLastQuestion ? "Finish Quiz" : "Next Question") 
                  : "Submit Answer")}
              </button>
            </div>
          )}
          
          {showFeedback && (
            <div className="question-stats">
              {/* <p>{question.percent_correct * 100}% of people got this question correct</p> */}
              {question.source && (
                <p className="source-citation">
                  Credit: <a href={question.source} target="_blank">{question.source}</a>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizQuestion;
